<template>
  <div class="canvasCard">
    <div class="cardHeader">
      <div class="headerLeft">
        <span class="cardTypeBadge" :class="card.card_type">{{ card.card_type }}</span>
        <span class="cardTitle">{{ card.title }}</span>
      </div>
      <div class="headerRight">
        <span class="cardTimestamp">{{ timeAgo }}</span>
        <button
          class="cardAction"
          :class="{ active: card.favorited_at }"
          title="Favorite"
          @click.stop="$emit('toggle-favorite', card)"
        >
          <AppIcon :name="card.favorited_at ? 'star_filled' : 'star'" :size="16" />
        </button>
        <button
          v-if="card.is_own"
          class="cardAction"
          :class="{ active: refreshing }"
          title="Refresh data"
          @click.stop="$emit('refresh', card)"
        >
          <svg :class="{ spinning: refreshing }" viewBox="0 0 20 20" fill="currentColor" width="16" height="16">
            <path fill-rule="evenodd" d="M15.312 11.424a5.5 5.5 0 01-9.201 2.466l-.312-.311h2.433a.75.75 0 000-1.5H4.598a.75.75 0 00-.75.75v3.634a.75.75 0 001.5 0v-2.033l.312.311a7 7 0 0011.712-3.138.75.75 0 00-1.449-.39zm-10.624-2.848a5.5 5.5 0 019.201-2.466l.312.311H11.77a.75.75 0 000 1.5h3.634a.75.75 0 00.75-.75V3.537a.75.75 0 00-1.5 0v2.033l-.312-.311A7 7 0 002.63 8.397a.75.75 0 001.449.39z" clip-rule="evenodd"/>
          </svg>
        </button>
        <button
          class="cardAction"
          title="Export"
          @click.stop="$emit('export', card)"
        >
          <AppIcon name="download" :size="16" />
        </button>
        <button
          v-if="card.is_own"
          class="cardAction danger"
          title="Delete"
          @click.stop="$emit('delete', card)"
        >
          <AppIcon name="trash" :size="16" />
        </button>
      </div>
    </div>
    <div class="cardBody">
      <slot />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import AppIcon from '../AppIcon.vue'
import type { CanvasCardSummary } from '../../services/canvas'

const props = defineProps<{
  card: CanvasCardSummary
  refreshing?: boolean
}>()

defineEmits<{
  'toggle-favorite': [card: CanvasCardSummary]
  'refresh': [card: CanvasCardSummary]
  'export': [card: CanvasCardSummary]
  'delete': [card: CanvasCardSummary]
}>()

const timeAgo = computed(() => {
  const d = new Date(props.card.updated_at || props.card.created_at)
  const now = Date.now()
  const diff = now - d.getTime()
  const mins = Math.floor(diff / 60_000)
  if (mins < 1) return 'just now'
  if (mins < 60) return `${mins}m ago`
  const hours = Math.floor(mins / 60)
  if (hours < 24) return `${hours}h ago`
  const days = Math.floor(hours / 24)
  if (days < 7) return `${days}d ago`
  return d.toLocaleDateString()
})
</script>

<style scoped>
.canvasCard {
  border: 1px solid var(--color-border, var(--border, #e5e7eb));
  border-radius: 8px;
  background: var(--color-bg-surface, var(--surface, #fff));
  overflow: hidden;
  transition: border-color 0.15s;
}
.canvasCard:hover {
  border-color: var(--color-border-strong, var(--border2, #d1d5db));
}
.cardHeader {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 12px;
  border-bottom: 1px solid var(--color-border, var(--border, #e5e7eb));
  gap: 8px;
}
.headerLeft {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
}
.cardTypeBadge {
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  padding: 2px 6px;
  border-radius: 4px;
  flex: 0 0 auto;
  width: fit-content;
  color: #fff;
}
.cardTypeBadge.table { background: #6366f1; }
.cardTypeBadge.chart { background: #10b981; }
.cardTypeBadge.text { background: #8b5cf6; }
.cardTypeBadge.metric { background: #f59e0b; }

.cardTitle {
  font-size: 14px;
  font-weight: 600;
  color: var(--color-text-primary, var(--text, #1a1a1a));
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.headerRight {
  display: flex;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;
}
.cardTimestamp {
  font-size: 11px;
  color: var(--color-text-secondary, var(--muted, #6b7280));
  margin-right: 4px;
}
.cardAction {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border: none;
  background: none;
  color: var(--color-text-secondary, var(--muted, #6b7280));
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.15s;
}
.cardAction:hover {
  background: var(--color-bg-hover, var(--hover-bg, #f3f4f6));
  color: var(--color-text-primary, var(--text, #1a1a1a));
}
.cardAction.active {
  color: #f59e0b;
}
.cardAction.danger:hover {
  color: var(--danger);
}
.spinning {
  animation: spin 1s linear infinite;
}
@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}
.cardBody {
  min-height: 60px;
}
</style>
